
import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CATEGORIES, STATES } from '../constants';
import ListingCard from '../components/ListingCard';
import { MapPin, Navigation, Building2, Search as SearchIcon, Loader2, PlusCircle, ShieldCheck, Zap, Home as HomeIcon } from 'lucide-react';
import { Professional } from '../types';
import { listingService } from '../services/listingService';
import SEOHandler from '../components/SEOHandler';

const DirectoryPage: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  const [searchTerm, setSearchTerm] = useState(queryParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(queryParams.get('category') || '');
  const [selectedState, setSelectedState] = useState(queryParams.get('state') || '');
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [filteredPros, setFilteredPros] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLocating, setIsLocating] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await listingService.getAllListings();
      setProfessionals(data);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    let results = [...professionals];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      results = results.filter(p => 
        p.businessName.toLowerCase().includes(lower) || 
        p.description.toLowerCase().includes(lower) ||
        p.location.suburb.toLowerCase().includes(lower)
      );
    }

    if (selectedCategory) {
      results = results.filter(p => p.category === selectedCategory);
    }

    if (selectedState) {
      results = results.filter(p => p.location.state === selectedState);
    }

    setFilteredPros(results);
  }, [searchTerm, selectedCategory, selectedState, professionals]);

  // Dynamic SEO Title and H1
  const categoryLabel = selectedCategory || 'Building & Planning';
  const locationLabel = selectedState ? `in ${selectedState}` : 'Australia';
  const pageTitle = `Top ${categoryLabel} Professionals ${locationLabel} | PlanStack`;
  const seoDescription = `Browse our vetted directory of the best ${categoryLabel} consultants ${locationLabel}. View profiles, reviews, and contact local experts today.`;

  const handleNearMe = () => {
    if (!navigator.geolocation) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSelectedState('VIC'); 
        setSearchTerm('');
        setIsLocating(false);
      },
      () => setIsLocating(false)
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHandler title={pageTitle} description={seoDescription} />
      
      <div className="bg-brand-900 py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
           <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
             <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
           </svg>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
           <div className="inline-flex items-center gap-2 bg-brand-800/50 backdrop-blur-sm px-4 py-2 rounded-full text-brand-200 text-[10px] font-black uppercase tracking-[0.2em] mb-6 border border-brand-700">
               <ShieldCheck size={14} className="text-accent-400" /> Australia's Premier Development Network
            </div>
           <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
             {categoryLabel} <span className="text-accent-400">{locationLabel}</span>
           </h1>
           <p className="text-brand-100 max-w-2xl mx-auto font-medium text-lg leading-relaxed mb-8">
             Connecting property developers with verified local expertise across {locationLabel}.
           </p>
           
           <div className="flex flex-wrap justify-center gap-4">
             <button onClick={handleNearMe} className="bg-white text-brand-900 font-black px-8 py-3 rounded-xl text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-brand-50 transition shadow-lg">
                <MapPin size={16} /> Locate Near Me
             </button>
             <Link to="/wizard" className="bg-accent-500 text-brand-900 font-black px-8 py-3 rounded-xl text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-accent-400 transition shadow-lg">
                <Zap size={16} /> AI Project Wizard
             </Link>
           </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-1/4">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 sticky top-24">
              <div className="space-y-8">
                <div className="pb-4 border-b border-gray-100">
                   <Link to="/" className="w-full bg-brand-50 text-brand-900 font-black px-4 py-3 rounded-xl text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-100 transition border border-brand-100">
                     <HomeIcon size={14} /> Home
                   </Link>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Refine Search</label>
                  <div className="relative">
                     <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                     <input type="text" className="w-full bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-brand-500 py-3 pl-10 text-sm font-medium" placeholder="Practice name or suburb..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Region</label>
                  <select className="w-full bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-brand-500 py-3 font-black text-xs uppercase tracking-widest" value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
                    <option value="">Any State</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Discipline</label>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar pr-2 text-[10px] font-black uppercase tracking-widest">
                     <button onClick={() => setSelectedCategory('')} className={`w-full text-left px-4 py-3 rounded-xl transition ${selectedCategory === '' ? 'bg-brand-900 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>All Disciplines</button>
                     {CATEGORIES.map(cat => (
                       <button key={cat} onClick={() => setSelectedCategory(cat)} className={`w-full text-left px-4 py-3 rounded-xl transition ${selectedCategory === cat ? 'bg-brand-900 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                         {cat}
                       </button>
                     ))}
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <main className="lg:w-3/4 space-y-6">
            <div className="flex items-center justify-between mb-2">
               <h2 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Showing Results for {categoryLabel} {locationLabel}</h2>
               <span className="text-[10px] font-black text-brand-600 bg-brand-50 px-3 py-1 rounded-full">{filteredPros.length} Firms Verified</span>
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm">
                <Loader2 className="animate-spin text-brand-600 mb-4" size={48} />
                <p className="text-gray-400 font-black uppercase tracking-widest text-[10px]">Syncing National Database...</p>
              </div>
            ) : filteredPros.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 animate-fade-in">
                {filteredPros.map(pro => (
                  <ListingCard key={pro.id} professional={pro} />
                ))}
              </div>
            ) : (
              <div className="text-center py-24 bg-white rounded-3xl border border-dashed border-gray-300 shadow-inner">
                <Building2 className="text-gray-200 mx-auto mb-6" size={64} />
                <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">No Records Found</h3>
                <Link to="/join" className="bg-brand-900 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition flex items-center justify-center gap-2 shadow-lg mt-8 inline-flex">
                  <PlusCircle size={16} /> Join the Network
                </Link>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DirectoryPage;
